import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  PowerIcon,
  SparklesIcon,
  PuzzlePieceIcon,
  ClockIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import GameRoomFormModal from '../components/gameRooms/GameRoomFormModal';
import gameRoomService from '../api/services/gameRoom.service';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/* =========================================================
   PATTERN PREVIEW HELPERS
   Maps each pattern key to a cell-highlighting function.
   `full` lights up the entire card.
========================================================= */
type PatternKey =
  | 'horizontal'
  | 'vertical'
  | 'diagonal'
  | 'singleCorner'
  | 'singleMiddle'
  | 'middleCross'
  | 'full';

const PATTERN_LABELS: Record<PatternKey, string> = {
  horizontal: 'Any Horizontal',
  vertical: 'Any Vertical',
  diagonal: 'Any Diagonal',
  singleCorner: '4 Single Corner',
  singleMiddle: '4 Single Middle',
  middleCross: '4 Middle Cross',
  full: 'Full House',
};

const PATTERN_CELLS: Record<PatternKey, (row: number, col: number) => boolean> = {
  horizontal: (row) => row === 1 || row === 3,
  vertical: (_, col) => col === 1 || col === 3,
  diagonal: (row, col) => row === col || row + col === 4,
  singleCorner: (row, col) =>
    (row === 0 || row === 4) && (col === 0 || col === 4),
  singleMiddle: (row, col) =>
    (row === 1 || row === 3) && (col === 1 || col === 3),
  middleCross: (row, col) =>
    (row === 1 && col === 2) ||
    (row === 3 && col === 2) ||
    (row === 2 && col === 1) ||
    (row === 2 && col === 3),
  // Full house — every cell is active
  full: () => true,
};

interface PatternPreviewProps {
  pattern: PatternKey;
}

const PatternPreview: React.FC<PatternPreviewProps> = ({ pattern }) => {
  const headers = ['B', 'I', 'N', 'G', 'O'];
  const isActive = PATTERN_CELLS[pattern];

  return (
    <div
      style={{ width: 170 }}
      className="p-2 bg-white border border-gray-200 rounded-lg shadow-xl"
    >
      <div
        className="grid grid-cols-5"
        style={{
          borderTop: '1.5px solid #4a4a4a',
          borderLeft: '1.5px solid #4a4a4a',
        }}
      >
        {headers.map((h) => (
          <div
            key={h}
            className="h-6 flex items-center justify-center text-[15px] font-bold text-gray-900"
            style={{
              borderRight: '1.5px solid #4a4a4a',
              borderBottom: '1.5px solid #4a4a4a',
            }}
          >
            {h}
          </div>
        ))}

        {Array.from({ length: 25 }).map((_, index) => {
          const row = Math.floor(index / 5);
          const col = index % 5;
          const isFreeSpace = row === 2 && col === 2;
          const active = isActive(row, col) && !isFreeSpace;

          return (
            <div
              key={`${row}-${col}`}
              className="aspect-square min-h-[26px] flex items-center justify-center"
              style={{
                borderRight: '1.5px solid #4a4a4a',
                borderBottom: '1.5px solid #4a4a4a',
                background: active ? '#f5f0eb' : isFreeSpace ? '#f7f7f7' : '#fff',
              }}
            >
              {isFreeSpace ? (
                <span className="text-[5px] font-bold text-gray-500 text-center leading-[1.1]">
                  FREE
                </span>
              ) : active ? (
                <span
                  className="w-[18px] h-[18px] rounded-full"
                  style={{
                    background: '#8B5A2B',
                    boxShadow:
                      'inset -2px -2px 3px rgba(255,255,255,0.12), 0 1px 2px rgba(0,0,0,0.2)',
                  }}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* =========================================================
   PAGE
========================================================= */
const GameRoomDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  const [hoveredPattern, setHoveredPattern] = useState<PatternKey | null>(null);
  const [popupPosition, setPopupPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const {
    data: room,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['game-room', id],
    queryFn: () => gameRoomService.getOne(id as string),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['game-room', id] });
    queryClient.invalidateQueries({ queryKey: ['game-rooms'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const handleToggleActive = async () => {
    if (!room) return;
    setTogglingActive(true);
    try {
      await gameRoomService.toggleActive(room._id);
      handleSuccess();
    } finally {
      setTogglingActive(false);
    }
  };

  const handleMouseEnter = (
    key: PatternKey,
    e: React.MouseEvent<HTMLElement>
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPopupPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
    setHoveredPattern(key);
  };

  const handleMouseLeave = () => {
    setHoveredPattern(null);
    setPopupPosition(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !room) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500">
          {error instanceof Error ? error.message : 'Game room not found'}
        </p>
        <button
          onClick={() => refetch()}
          className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
        >
          Retry
        </button>
        <div className="mt-3">
          <button
            onClick={() => navigate('/game-management')}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            ← Back to Game Management
          </button>
        </div>
      </div>
    );
  }

  const statusBadge = !room.isActive
    ? { label: 'Disabled', dot: 'bg-gray-400', bg: 'bg-gray-100', text: 'text-gray-600' }
    : room.status === 'running'
    ? { label: 'Live', dot: 'bg-green-500', bg: 'bg-green-50', text: 'text-green-700' }
    : room.status === 'waiting'
    ? { label: 'Waiting', dot: 'bg-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-700' }
    : { label: 'Finished', dot: 'bg-gray-400', bg: 'bg-gray-100', text: 'text-gray-600' };

  const activePatterns = Object.entries(room.patterns)
    .filter(([, v]) => v)
    .map(([k]) => k);

  return (
    <div>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/game-management')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              {room.isSuperBingo ? (
                <SparklesIcon className="w-5 h-5 text-primary-500" />
              ) : (
                <PuzzlePieceIcon className="w-5 h-5 text-secondary-500" />
              )}
              {room.roomId}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {room.isSuperBingo ? 'Super Bingo Room' : 'Standard Room'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleActive}
            disabled={togglingActive}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              room.isActive
                ? 'bg-white text-red-600 border-red-200 hover:bg-red-50'
                : 'bg-white text-green-600 border-green-200 hover:bg-green-50'
            } disabled:opacity-50`}
          >
            <PowerIcon className="w-3.5 h-3.5" />
            {togglingActive ? 'Updating...' : room.isActive ? 'Disable' : 'Enable'}
          </button>
          <Button size="sm" onClick={() => setIsEditOpen(true)}>
            <PencilSquareIcon className="w-4 h-4 mr-1.5" />
            Edit
          </Button>
        </div>
      </div>

      {/* Status + key stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card padding="sm">
          <p className="text-xs text-gray-500 mb-1">Status</p>
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${statusBadge.dot}`} />
            {statusBadge.label}
          </span>
        </Card>
        <Card padding="sm">
          <p className="text-xs text-gray-500 mb-1">Bet Amount</p>
          <p className="text-lg font-bold text-gray-900">
            ETB {room.betAmount.toLocaleString()}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-xs text-gray-500 mb-1">Commission</p>
          <p className="text-lg font-bold text-gray-900">{room.commissionPercent}%</p>
        </Card>
        <Card padding="sm">
          <p className="text-xs text-gray-500 mb-1">Players</p>
          <p className="text-lg font-bold text-gray-900">{room.playersCount}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: description, rules, patterns */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <h2 className="text-sm font-semibold text-gray-900 mb-3">
              Description
            </h2>
            <p className="text-sm text-gray-600">
              {room.description || '—'}
            </p>

            {room.rules && (
              <>
                <h3 className="text-sm font-semibold text-gray-900 mt-5 mb-2">
                  Rules
                </h3>
                <p className="text-sm text-gray-600">{room.rules}</p>
              </>
            )}
          </Card>

          <Card>
            <h2 className="text-sm font-semibold text-gray-900 mb-3">
              Winning Patterns
            </h2>
            {activePatterns.length === 0 ? (
              <p className="text-xs text-gray-500">No patterns enabled.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(room.patterns).map(([key, enabled]) => {
                  const patternKey = key as PatternKey;
                  const label = PATTERN_LABELS[patternKey] || key;

                  return (
                    <span
                      key={key}
                      onMouseEnter={(e) =>
                        enabled && handleMouseEnter(patternKey, e)
                      }
                      onMouseLeave={handleMouseLeave}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        enabled
                          ? 'bg-primary-50 text-primary-700 cursor-help hover:bg-primary-100'
                          : 'bg-gray-50 text-gray-400 line-through'
                      }`}
                    >
                      {enabled && <CheckIcon className="w-3 h-3" />}
                      <span className="capitalize">{label}</span>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                Patterns required to win:{' '}
                <span className="font-semibold text-gray-900">
                  {room.patternsRequiredToWin}
                </span>
              </p>
            </div>
          </Card>
        </div>

        {/* Right: super bingo schedule or standard info */}
        <div className="space-y-4">
          <Card>
            <h2 className="text-sm font-semibold text-gray-900 mb-3">
              Configuration
            </h2>
            <div className="space-y-2 text-xs">
              <Row label="Call Interval" value={`${room.callIntervalSeconds}s`} />
              <Row
                label="Patterns To Win"
                value={String(room.patternsRequiredToWin)}
              />
              <Row
                label="Max Cartela Count"
                value={String(room.maxCartelaCount ?? 500)}
              />
              <Row label="Prize Pool" value={`ETB ${room.prizePool.toLocaleString()}`} />
              <Row label="Type" value={room.isSuperBingo ? 'Super Bingo' : 'Standard'} />
            </div>
          </Card>

          {room.isSuperBingo && (
            <Card>
              <div className="flex items-center gap-2 mb-3">
                <ClockIcon className="w-4 h-4 text-primary-500" />
                <h2 className="text-sm font-semibold text-gray-900">
                  Super Bingo Schedule
                </h2>
              </div>

              {room.schedule.length === 0 ? (
                <p className="text-xs text-gray-500">No slots configured.</p>
              ) : (
                <div className="space-y-1.5">
                  {room.schedule.map((slot, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-primary-50/50 border border-primary-100 rounded-lg px-2.5 py-1.5"
                    >
                      <span className="text-xs font-medium text-primary-700">
                        {DAYS[slot.day]}
                      </span>
                      <span className="text-xs text-gray-700">
                        {String(slot.hour).padStart(2, '0')}:
                        {String(slot.minute).padStart(2, '0')}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {room.scheduledStartAt && (
                <div className="mt-3 pt-3 border-t border-primary-100">
                  <p className="text-[10px] text-gray-500">Next Scheduled Start</p>
                  <p className="text-xs font-medium text-gray-900">
                    {new Date(room.scheduledStartAt).toLocaleString()}
                  </p>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* Edit modal */}
      <GameRoomFormModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSuccess={handleSuccess}
        room={room}
      />

      {/* Hover pattern preview */}
      {hoveredPattern && popupPosition && (
        <div
          style={{
            position: 'fixed',
            top: popupPosition.top,
            left: popupPosition.left,
            transform: 'translateY(-50%)',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          <PatternPreview pattern={hoveredPattern} />
        </div>
      )}
    </div>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium text-gray-900">{value}</span>
  </div>
);

export default GameRoomDetail;